import { defineChain, type Chain } from "viem";
import { base, mainnet } from "wagmi/chains";

/** Robinhood Chain isn't in wagmi's built-in list, so define it here. */
export const robinhood = defineChain({
  id: 4663,
  name: "Robinhood Chain",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: {
    default: { http: ["https://rpc.mainnet.chain.robinhood.com"] },
  },
  blockExplorers: {
    default: { name: "Blockscout", url: "https://robinhoodchain.blockscout.com" },
  },
  testnet: false,
});

export const CHAINS: Record<string, Chain> = { base, mainnet, robinhood };

const selected = (import.meta.env.VITE_CHAIN as string) || "base";

/** Default chain for collections that don't specify their own. */
export const targetChain: Chain = CHAINS[selected] ?? base;

const ZERO = "0x0000000000000000000000000000000000000000";

export const contractAddress = (import.meta.env.VITE_CONTRACT_ADDRESS || ZERO) as `0x${string}`;
export const collectionName = import.meta.env.VITE_COLLECTION_NAME || "ClayCats Club";
export const collectionSymbol = import.meta.env.VITE_COLLECTION_SYMBOL || "CCC";

export interface CollectionConfig {
  name: string;
  address: `0x${string}`;
  chain: Chain;
}

/**
 * One site can mint several collections — e.g. a free pixel drop and a paid
 * wizard drop. Provide them as a JSON array in VITE_COLLECTIONS:
 *
 *   VITE_COLLECTIONS=[{"name":"Pixel Wizards","address":"0x..","chain":"base"},
 *                     {"name":"High Wizards","address":"0x..","mintPrice":"0.1"}]
 *
 * Falls back to the single VITE_CONTRACT_ADDRESS when VITE_COLLECTIONS is unset.
 */
function parseCollections(): CollectionConfig[] {
  const raw = import.meta.env.VITE_COLLECTIONS;
  if (raw) {
    try {
      const arr = JSON.parse(raw) as Array<{ name?: string; address?: string; chain?: string }>;
      return arr
        .filter((c) => c.address && c.address !== ZERO)
        .map((c) => ({
          name: c.name || collectionName,
          address: c.address as `0x${string}`,
          chain: c.chain ? CHAINS[c.chain] ?? targetChain : targetChain,
        }));
    } catch (err) {
      console.warn("VITE_COLLECTIONS is not valid JSON; ignoring.", err);
    }
  }
  if (contractAddress !== ZERO) {
    return [{ name: collectionName, address: contractAddress, chain: targetChain }];
  }
  return [];
}

export const collections = parseCollections();
export const isConfigured = collections.length > 0;
